import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { documentRevisionAuditTreatment } from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import {
  generateRevisionAuditPdf,
  type RevisionAuditFlags,
} from 'src/services/documentService/revisionAudit';
import { isArchivedNote } from './archive-note';

/**
 * BRK-2036 — the "Generate / View Document With Revisions" pair on the Completed Documents
 * kebab menu (epic BRK-2028).
 *
 * The rules live here rather than inline in the menu for the reason applyArchivedReadOnly
 * gives: a document list is not the only surface these could ever appear on, and the two
 * items' visibility is a single three-flag rule that has to stay one rule. Same shape as
 * useArchiveNote / useHoldFromBilling — predicates plus the action they gate.
 */

/** The row as this feature reads it. Rows are untyped `any` in the lists; these are the fields we add. */
export type RevisionAuditRow = RevisionAuditFlags & {
  uuid?: string;
  documentType?: string;
};

const hasExport = (document: RevisionAuditRow | null | undefined) =>
  Boolean(document?.revisionAuditPdfUrl);

/**
 * The document handed to the existing preview route for the with-revisions PDF. preview-document
 * reads only `pdfUrl`, `documentType` and the sibling `patientData`, so spreading the row and
 * swapping those two is enough — and keeping the rest means the route's state stays the shape
 * every other caller passes.
 */
export const toRevisionAuditPreview = <T extends RevisionAuditRow>(document: T) => ({
  ...document,
  pdfUrl: document.revisionAuditPdfUrl,
  documentType: `${document.documentType} — With Revisions`,
});

type UseRevisionAuditOptions = {
  /**
   * Called after a successful generate. Pass the LOCAL list refetch: the fresh row carries
   * `revisionAuditPdfUrl` and the menu flips from Generate to View.
   */
  onGenerated?: () => void;
};

export const useRevisionAudit = (options?: UseRevisionAuditOptions) => {
  const { onGenerated } = options ?? {};

  //  Fails closed: the hook answers null until the Split SDK is ready and false for control,
  //  so anything but a strict `true` keeps both items hidden.
  const [isRevisionAuditEnabled] = useFeatureToggleWhenReady(
    documentRevisionAuditTreatment,
    'FACILITY'
  );
  const isEnabled = isRevisionAuditEnabled === true;

  //  Two records of the same thing on purpose. The ref is the guard: it is written
  //  synchronously, so a second click landing in the same tick as the first — before React has
  //  re-rendered — still sees the document as in flight and cannot fire a second POST. The state
  //  is what takes the menu item away, which needs a render to happen at all.
  const inFlight = useRef<Set<string>>(new Set());
  const [generating, setGenerating] = useState<string[]>([]);

  const isGenerating = (document: RevisionAuditRow | null | undefined) =>
    Boolean(document?.uuid) && generating.includes(document!.uuid!);

  /**
   * A generate is offered when the document has an edit-after-signing history at all and what we
   * have stored is either nothing or out of date. `revisionAuditStale` is what makes the pair
   * appear together: the old export stays viewable while a current one can be asked for.
   *
   * Archived notes are excluded here as well as by applyArchivedReadOnly — an archived note's
   * submission is hard-deleted underneath, so a generate against it could only fail, and that
   * should not depend on which surface renders the item (BOPS-151/175 fixed the same gap in
   * useHoldFromBilling after the Daily Notes page rendered its predicates individually).
   */
  const canGenerate = (document: RevisionAuditRow | null | undefined) =>
    isEnabled &&
    document?.revisionAuditEligible === true &&
    (!hasExport(document) || document?.revisionAuditStale === true) &&
    !isArchivedNote(document) &&
    !isGenerating(document);

  /**
   * A stored export is viewable whenever one exists — including a stale one, which is still the
   * revision history as of the last generate. The caller ANDs its own read gate on top, the same
   * one it already applies to View Document.
   */
  const canView = (document: RevisionAuditRow | null | undefined) =>
    isEnabled && hasExport(document) && !isArchivedNote(document);

  const requestGenerate = async (document: RevisionAuditRow | null | undefined) => {
    const formSubmissionUuid = document?.uuid;
    if (!formSubmissionUuid || !canGenerate(document)) return;
    if (inFlight.current.has(formSubmissionUuid)) return;

    inFlight.current.add(formSubmissionUuid);
    setGenerating((prev) => [...prev, formSubmissionUuid]);
    //  ~3-6s server-side (trail walk, diff, render, merge), so the wait needs saying.
    const toastId = toast.loading('Generating the document with revisions…');

    try {
      await generateRevisionAuditPdf(formSubmissionUuid);
      toast.success('The document with revisions is ready.', { id: toastId });
      onGenerated?.();
    } catch (error: any) {
      //  The endpoint explains its own refusals — a zero-change resubmit, a document type not
      //  in the allowlist, the treatment off server-side — so its message beats a generic one.
      toast.error(
        error?.message || 'Could not generate the document with revisions. Please try again.',
        { id: toastId }
      );
    } finally {
      inFlight.current.delete(formSubmissionUuid);
      setGenerating((prev) => prev.filter((uuid) => uuid !== formSubmissionUuid));
    }
  };

  return { canGenerate, canView, isGenerating, requestGenerate };
};
